import React, { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';
import DaySelectionGrid from './DaySelectionGrid.react';
import RepeatSummary from './RepeatSummary.react';
import { useMealRepetition } from '../../hooks/useMealRepetition';
import { useMealPlanData, useAssets } from '../../contexts';
import './MealRepeatModal.scss';

function MealRepeatModal({ meals, closeModal, addMeals, applyOverlapResolution, modalManager }) {
    const { profile, plan } = useMealPlanData();
    const { recipes, foods } = useAssets();

    const repetition = useMealRepetition({
        sourceMeals: meals,
        existingPlan: plan,
        profile,
        recipes,
        foods,
        onComplete: (modifiedMeals) => {
            addMeals(modifiedMeals);
            closeModal(true);
        },
        onError: console.error
    });

    const handleRepeat = useCallback(async () => {
        const result = await repetition.processRepetition();
        if (result === false) return;
        
        if (result.hasOverlaps) {
            // Open the overlap modal using the modal manager
            modalManager.openOverlapModal({
                overlaps: result.overlaps,
                clears: result.clears,
                meals: meals,
                isRepeatMeal: true,
                applyOverlapResolution: (dirtyMeals, mealsToDelete, leftoverOffsets) => {
                    // Handle the overlap resolution and then complete the repetition
                    const resolutionResult = applyOverlapResolution(dirtyMeals, mealsToDelete, leftoverOffsets);
                    if (resolutionResult?.success) {
                        // Complete the repetition flow with empty meals since applyOverlapResolution already handled the updates
                        repetition.completeRepetition([]);
                    }
                    return resolutionResult;
                }
            });
        }
    }, [repetition, modalManager, meals, applyOverlapResolution]);

    const handleClose = useCallback((allDone) => {
        if (allDone === true) return closeModal();
        closeModal();
    }, [closeModal]);

    if (!meals?.length) return null;

    return (
        <>
            <Modal 
                isOpen={true}
                onRequestClose={handleClose}
                className="el-modal el-modal2"
                contentLabel="Repeat Meal"
                overlayClassName="el-modal-overlay"
                closeTimeoutMS={250}
            >
                <section className="el-modal-container el-modal2-container">
                    <header>
                        <h2>Repeat Meal</h2>
                        <button onClick={handleClose} className="el-modal-close-x">
                            <i className="icon-close-x" />
                            <span className="assistive-text">Close Modal</span>
                        </button>
                    </header>

                    <div className="el-modal-body-container el-modal2-body-container">
                        <RepeatSummary
                            titles={repetition.titles || []}
                            selections={repetition.selections}
                            validationErrors={repetition.validationErrors}
                            hasOverlaps={repetition.hasOverlaps}
                            overlapCount={repetition.overlaps.length}
                        />

                        <DaySelectionGrid
                            ogOffset={repetition.ogOffset}
                            ogMealType={repetition.ogMealType}
                            idsToRepeat={repetition.idsToRepeat}
                            onToggleSelection={repetition.toggleSelection}
                            isSelected={repetition.isSelected}
                            isDisabled={repetition.isDisabled}
                        />
                    </div>

                    <footer>
                        {repetition.error && <p className="error-msg">{repetition.error}</p>}
                        <button className="el-modal-cancel-btn" onClick={handleClose}>Cancel</button>
                        <button 
                            className="el-modal-ok-btn" 
                            onClick={handleRepeat}
                            disabled={repetition.isProcessing || !repetition.hasSelections}
                        >
                            {repetition.isProcessing ? 'Processing...' : 'Save'}
                        </button>
                    </footer>
                </section>
            </Modal>
        </>
    );
}

MealRepeatModal.propTypes = {
    meals: PropTypes.array,
    closeModal: PropTypes.func.isRequired,
    addMeals: PropTypes.func.isRequired,
    applyOverlapResolution: PropTypes.func.isRequired,
    modalManager: PropTypes.object.isRequired,
};

export default MealRepeatModal;